import { Button } from '@v2/ui/Button';
import { Calendar, Day } from '@v2/ui/Calendar';
import { ArrowLeft, ArrowRight } from '@v2/ui/Icon/Arrow';
import { CalendarIcon } from '@v2/ui/Icon/Time';
import { Popover, PopoverContent, PopoverTrigger } from '@v2/ui/Popover';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';

interface DateRangePickerProps {
  selectedDateFilter?: DateRange;
  onDateChange: (dateFilter: DateRange) => void;
  defaultLabel?: string;
  compactMobile?: boolean;
}

export function DateRangePicker({
  selectedDateFilter,
  onDateChange,
  defaultLabel = 'All dates',
  compactMobile = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [date, setDate] = useState<DateRange | undefined>(selectedDateFilter);

  useEffect(() => {
    if (!selectedDateFilter?.from && !selectedDateFilter?.to) {
      setDate({ from: undefined, to: undefined });
    }
  }, [selectedDateFilter]);

  const handleDateChange = (newDate: DateRange | undefined) => {
    if (newDate?.from && newDate?.to && isSameDay(newDate.from, newDate.to))
      return;
    setDate(newDate);
  };

  const formatDateRange = () => {
    if (!date?.from) return defaultLabel;

    if (!date.to) {
      return format(date.from, 'MMM d, yyyy');
    }

    return `${format(date.from, 'MMM d')} - ${format(date.to, 'MMM d, yyyy')}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className={`flex h-9 items-center gap-x-2 font-normal ${
            compactMobile
              ? 'min-w-0 px-3 text-sm md:h-12 md:px-4 md:text-base'
              : 'md:h-12'
          }`}
        >
          <CalendarIcon className="text-gray-800" />
          <span>{formatDateRange()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          month={currentMonth}
          onMonthChange={(month) => setCurrentMonth(month)}
          weekStartsOn={1}
          initialFocus
          mode="range"
          selected={date}
          onSelect={handleDateChange}
          disabled={{ before: new Date() }}
          components={{
            IconLeft: () => <ArrowLeft size="20" className="stroke-gray-800" />,
            IconRight: () => (
              <ArrowRight size="20" className="stroke-gray-800" />
            ),
            Day: ({ ...props }) => <Day {...props} />,
            Footer: ({ displayMonth }) => (
              <CalendarFooter
                onCancel={() => setIsOpen(false)}
                displayMonth={displayMonth}
                date={date}
                setDate={setDate}
                onConfirm={() => {
                  setIsOpen(false);
                  onDateChange(date);
                }}
                currentMonth={currentMonth}
              />
            ),
          }}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

function CalendarFooter({
  setDate,
  date,
  onConfirm,
  displayMonth,
  currentMonth,
  onCancel,
}: {
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  date: DateRange | undefined;
  onConfirm: () => void;
  displayMonth: Date | undefined;
  currentMonth: Date;
  onCancel: () => void;
}) {
  if (!displayMonth) return null;
  const isFirstMonth = isSameMonth(displayMonth, currentMonth);

  return (
    <tfoot className="flex items-center justify-between py-2">
      {isFirstMonth && (
        <Button
          variant="tertiary"
          className="text-gray-800"
          onClick={() => setDate({ from: undefined, to: undefined })}
        >
          Reset
        </Button>
      )}
      {!isFirstMonth && (
        <div className="flex w-full justify-end space-x-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!date?.from && !date?.to} onClick={onConfirm}>
            Apply
          </Button>
        </div>
      )}
    </tfoot>
  );
}
